import type React from "react";
import { useState } from "react";

// Mirrors the backend payload from
// agent_runtime/surfaces/superdoc.py:_handle_staged_edits_event.
export interface StagedEditItem {
  id: string;
  instruction: string;
  section?: string;
}

export interface StagedEditsData {
  audio_file_id: number;
  edits: StagedEditItem[];
  count: number;
}

type CustomAction = {
  type: string;
  payload?: Record<string, unknown>;
};

interface StagedEditsWidgetProps {
  data: StagedEditsData;
  // Posts a threads.custom_action back to the chat endpoint. Both
  // buttons converge on the same backend apply/discard path the agent
  // tools use — the click IS the explicit sign-off.
  onCustomAction: (action: CustomAction) => void;
  disabled?: boolean;
}

type State = { kind: "pending" } | { kind: "resolved"; action: "apply" | "discard" };

const StackIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
    <path
      d="M8 1.5L14.5 4.75L8 8L1.5 4.75L8 1.5Z"
      stroke="#6366F1"
      strokeWidth="1.3"
      fill="#EEF2FF"
      strokeLinejoin="round"
    />
    <path d="M2 8L8 11L14 8" stroke="#6366F1" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    <path
      d="M2 11.25L8 14.25L14 11.25"
      stroke="#6366F1"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * The batched-edit "change set" card: lists the note edits the clinician
 * has queued (via stage_note_edit) but not yet applied, with an explicit
 * "Apply all changes" sign-off and a "Discard". Nothing regenerates the
 * note until Apply is clicked (or the clinician tells Superdoc to go
 * ahead in chat). One apply = one new note version with every queued
 * change merged.
 */
const StagedEditsWidget: React.FC<StagedEditsWidgetProps> = ({ data, onCustomAction, disabled }) => {
  const [state, setState] = useState<State>({ kind: "pending" });
  // Optimistically hide items the clinician removed via the per-item "×",
  // before the backend re-emits the refreshed queue.
  const [removedIds, setRemovedIds] = useState<Set<string>>(new Set());

  if (!data) return null;
  const edits = (data.edits || []).filter((e) => !removedIds.has(e.id));
  // An empty queue (e.g. emitted after a discard/apply, or every item
  // removed) and nothing resolved locally — render nothing rather than
  // an empty card.
  if (state.kind === "pending" && edits.length === 0) return null;

  if (state.kind === "resolved") {
    return (
      <div style={{ ...styles.container, ...styles.containerResolved }}>
        <div style={styles.header}>
          <StackIcon />
          <span style={styles.headerText}>
            {state.action === "apply"
              ? "Applying your changes — a new version is generating…"
              : "Discarded the queued changes"}
          </span>
        </div>
      </div>
    );
  }

  const run = (action: "apply" | "discard") => {
    if (disabled) return;
    onCustomAction({
      type: action === "apply" ? "apply_staged_edits" : "discard_staged_edits",
      payload: { audio_file_id: data.audio_file_id },
    });
    setState({ kind: "resolved", action });
  };

  const removeOne = (editId: string) => {
    if (disabled) return;
    onCustomAction({
      type: "remove_staged_edit",
      payload: { audio_file_id: data.audio_file_id, edit_id: editId },
    });
    setRemovedIds((prev) => new Set(prev).add(editId));
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <StackIcon />
        <span style={styles.headerText}>Pending changes</span>
        <span style={styles.countBadge}>{edits.length}</span>
      </div>
      <ul style={styles.list}>
        {edits.map((edit) => (
          <li key={edit.id} style={styles.listItem}>
            <div style={styles.itemBody}>
              {edit.section ? <span style={styles.sectionTag}>{edit.section}</span> : null}
              <span>{edit.instruction}</span>
            </div>
            <button
              type="button"
              disabled={disabled}
              onClick={() => removeOne(edit.id)}
              aria-label="Remove this change"
              title="Remove this change"
              style={{ ...styles.removeButton, ...(disabled ? styles.buttonDisabled : {}) }}
            >
              ×
            </button>
          </li>
        ))}
      </ul>
      <div style={styles.hint}>The note is unchanged until you apply these.</div>
      <div style={styles.actionsRow}>
        <button
          type="button"
          disabled={disabled}
          onClick={() => run("apply")}
          style={{ ...styles.button, ...styles.applyButton, ...(disabled ? styles.buttonDisabled : {}) }}
        >
          Apply all changes
        </button>
        <button
          type="button"
          disabled={disabled}
          onClick={() => run("discard")}
          style={{ ...styles.button, ...styles.discardButton, ...(disabled ? styles.buttonDisabled : {}) }}
        >
          Discard
        </button>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    padding: "12px 14px",
    marginTop: "8px",
    borderRadius: "12px",
    border: "1px solid #C7D2FE",
    backgroundColor: "#F5F3FF",
  },
  containerResolved: {
    backgroundColor: "#F9FAFB",
    border: "1px solid #E5E7EB",
    paddingTop: "10px",
    paddingBottom: "10px",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  headerText: {
    fontSize: "13px",
    fontWeight: 600,
    color: "#111827",
  },
  countBadge: {
    marginLeft: "auto",
    fontSize: "11px",
    fontWeight: 600,
    color: "#4338CA",
    backgroundColor: "#E0E7FF",
    padding: "2px 8px",
    borderRadius: "999px",
  },
  list: {
    margin: 0,
    padding: 0,
    listStyle: "none",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  listItem: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "8px",
    fontSize: "13px",
    color: "#1F2937",
    lineHeight: "1.4",
  },
  itemBody: {
    display: "flex",
    alignItems: "baseline",
    gap: "6px",
    flex: 1,
    minWidth: 0,
  },
  removeButton: {
    flexShrink: 0,
    width: "20px",
    height: "20px",
    lineHeight: "16px",
    padding: 0,
    fontSize: "16px",
    color: "#9CA3AF",
    backgroundColor: "transparent",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontFamily: "inherit",
  },
  sectionTag: {
    fontSize: "10px",
    fontWeight: 600,
    textTransform: "uppercase",
    color: "#4338CA",
    backgroundColor: "#E0E7FF",
    padding: "1px 6px",
    borderRadius: "4px",
    marginRight: "6px",
  },
  hint: {
    fontSize: "12px",
    color: "#6B7280",
  },
  actionsRow: {
    display: "flex",
    gap: "8px",
    marginTop: "4px",
  },
  button: {
    flex: "0 1 auto",
    padding: "8px 14px",
    fontSize: "13px",
    fontWeight: 500,
    borderRadius: "8px",
    border: "1px solid transparent",
    cursor: "pointer",
    fontFamily: "inherit",
    transition: "background-color 0.15s",
  },
  applyButton: {
    backgroundColor: "#4338CA",
    color: "#FFFFFF",
    borderColor: "#4338CA",
  },
  discardButton: {
    backgroundColor: "#FFFFFF",
    color: "#374151",
    borderColor: "#D1D5DB",
  },
  buttonDisabled: {
    opacity: 0.6,
    cursor: "not-allowed",
  },
};

export default StagedEditsWidget;
